class DrawerMenu extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.el = this;
    this.mainLevel = this.el.querySelector('.drawer-menu__contents');
    this.init();
  }

  init() {
    // Level 1 inputs and the stores panel are in the page HTML; the level 1 panels (with the level 2 inputs and
    // back buttons) may be loaded later by loadPanels(), so the panel controls use event delegation.
    this.firstLevelItemsWithChildren = this.el.querySelectorAll('.drawer-menu__item__input');

    // These toggle submenu and subsubmenu panels
    this.el.addEventListener('change', (event) => {
      const input = event.target.closest('.drawer-menu__item__input, .drawer-submenu__item__input');
      if (input) this.toggleMenuItem(input);
    });

    this.el.addEventListener('click', (event) => {
      // These handle going back to previous panel
      const backButton = event.target.closest('[data-drawer-submenu-close], [data-drawer-subsubmenu-close]');
      if (backButton) {
        this.toggleSubPanel(event, backButton);
        return;
      }

      // These handle closing of navigation
      if (event.target.closest('[data-close]')) {
        this.closeAllItems(event);
      }
    });

    // Note: Drawer closing is now handled centrally in Woolman.ModalsAndDrawers.showModalOrDrawer
    // which ensures only one drawer can be open at a time

    this.overlay = this.el.querySelector('[data-overlay]');
    this.overlay.addEventListener('click', this.closeAllItems.bind(this));

    // Handle opening of specific subitem from anywhere in the theme
    this.subitemOpeners = document.querySelectorAll('[data-drawer-submenu-open]');
    this.subitemOpeners.forEach((button) => {
      button.addEventListener('click', this.toggleMenuItemFromTheme.bind(this));
    });

    this.initPanelLoading();
  }

  /**
   * The level 1 panels are left out of the page HTML (snippets/drawer-menu.liquid) and loaded from
   * data-panels-url: as soon as the user shows intent to open the menu, and when the browser is idle on
   * screens where the drawer is the main navigation.
   */
  initPanelLoading() {
    this.panelsUrl = this.el.dataset.panelsUrl;
    if (!this.panelsUrl) return;

    const load = () => this.loadPanels();
    const intentEvents = ['pointerenter', 'touchstart', 'focusin'];
    document.querySelectorAll('[href="#drawer-menu"], [data-drawer-submenu-open]').forEach((opener) => {
      intentEvents.forEach((type) => opener.addEventListener(type, load, { once: true, passive: true }));
    });

    if (window.matchMedia('(max-width: 1279px)').matches) {
      const loadWhenIdle = () => ('requestIdleCallback' in window ? requestIdleCallback(load) : setTimeout(load, 2000));
      if (document.readyState === 'complete') {
        loadWhenIdle();
      } else {
        window.addEventListener('load', loadWhenIdle, { once: true });
      }
    }
  }

  /**
   * Resolves to true once every level 1 panel is in the page, false if they couldn't be loaded.
   * A failed load can be retried: the next call starts a new request.
   */
  loadPanels() {
    if (!this.panelsUrl || !this.el.querySelector('[data-lazy-panel]')) return Promise.resolve(true);

    if (!this.panelsPromise) {
      this.panelsPromise = this.fetchAndInsertPanels().then((loaded) => {
        if (!loaded) this.panelsPromise = null;
        return loaded;
      });
    }

    return this.panelsPromise;
  }

  async fetchAndInsertPanels() {
    const cacheKey = `drawer-menu-panels:${this.panelsUrl}:${this.el.dataset.panelsVersion}`;

    try {
      let cached = null;
      try {
        cached = sessionStorage.getItem(cacheKey);
      } catch (error) {}

      if (cached) {
        this.insertPanels(cached);
        if (!this.el.querySelector('[data-lazy-panel]')) return true;
        // The cached panels don't match this page's menu, so fetch them again
      }

      const response = await fetch(this.panelsUrl);
      if (!response.ok) throw new Error(`Drawer menu panels: ${response.status}`);
      const html = await response.text();
      this.insertPanels(html);

      try {
        // Drop panels cached for an older menu version, then cache these
        Object.keys(sessionStorage)
          .filter((key) => key.startsWith('drawer-menu-panels:') && key !== cacheKey)
          .forEach((key) => sessionStorage.removeItem(key));
        sessionStorage.setItem(cacheKey, html);
      } catch (error) {}

      return !this.el.querySelector('[data-lazy-panel]');
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  insertPanels(html) {
    const panels = new DOMParser().parseFromString(html, 'text/html');
    this.el.querySelectorAll('[data-lazy-panel]').forEach((placeholder) => {
      const panel = panels.getElementById(placeholder.id);
      if (panel) placeholder.replaceWith(document.adoptNode(panel));
    });
  }

  /**
   * Waits for the item's panel if it hasn't been loaded yet. If it can't be loaded, the item can't show
   * its links, so this goes to the item's page instead and resolves to false.
   * fromMenu: the item was opened in the drawer, so only go if it's still open when the request fails.
   */
  async ensurePanel(input, fromMenu = false) {
    const accordion = input.closest('[data-accordion-item]');
    if (!accordion || !accordion.querySelector(':scope > [data-lazy-panel]')) return true;

    const loaded = await this.loadPanels();
    if (loaded || !accordion.querySelector(':scope > [data-lazy-panel]')) return true;

    // Only if the shopper is still waiting for this item: not after closing the menu or picking another item
    const stillOpen = this.classList.contains('is-open') && accordion.classList.contains('is-open');
    const href = input.getAttribute('data-href');
    if ((stillOpen || !fromMenu) && href && href !== '#') window.location.href = href;
    return false;
  }

  closeAllItems(event, skipEl, target) {
    if (!target) {
      target = this.mainLevel;
    }

    const checkboxes = target.querySelectorAll('input:checked');
    const childPanels = target.querySelectorAll('[data-menu-panel]');

    checkboxes.forEach((checkbox) => {
      if (skipEl && checkbox === skipEl) {
        return;
      }
      checkbox.checked = false;
      const accordion = checkbox.closest('[data-accordion-item]');
      if (accordion) {
        accordion.classList.remove('is-open');
        if (screen.width <= 820) {
          const parent = accordion.closest('.drawer-menu__contents');
          parent.classList.remove('pull-left');
        }
      }
    });

    childPanels.forEach((panel) => panel.classList.remove('has-open-submenu'));

    if (!skipEl) {
      this.mainLevel.classList.remove('has-open-submenu');
    }
  }

  toggleMenuItem(input) {
    if (!input) return;

    const level = +input.getAttribute('data-current-level');
    const target = level === 1 ? this.mainLevel : input.closest('[data-menu-panel]');
    this.closeAllItems(null, input, target);

    const accordion = input.closest('[data-accordion-item]');
    if (!accordion) {
      return;
    }
    
    accordion.classList.toggle('is-open');
    if (accordion.classList.contains('is-open')) {
      target.classList.add('has-open-submenu');
      this.ensurePanel(input, true);
    } else {
      target.classList.remove('has-open-submenu');
    }
  }

  async toggleMenuItemFromTheme(scope) {
    let target = false;
    const button = scope.currentTarget || scope;
    const url = button.getAttribute('data-drawer-submenu-open');

    for (const input of this.firstLevelItemsWithChildren) {
      if (input.getAttribute('data-href') === url) {
        target = input;
        break;
      }
    }

    if (!target) return;

    // Close the drawer and all its children before opening a new panel
    if (this.classList.contains('is-open') && !target.checked) {
      Woolman.ModalsAndDrawers.closeModalOrDrawer(this.el.getAttribute('id'));
      this.closeAllItems();
    }

    // Only menu items wait for their panel; the localization panel is always in the page
    if (!(await this.ensurePanel(target))) return;

    // Get the first navigation item in the panel to focus on when the panel is opened
    const parent = target.closest('[data-accordion-item]');
    const focusTarget = parent.querySelector('[data-accordion-panel] [data-navigation-item]');

    Woolman.ModalsAndDrawers.showModalOrDrawer(this.el.getAttribute('id'), null, null, focusTarget);

    const animationFinished = () => {
      this.el.removeEventListener('transitionend', animationFinished);

      const evt = new Event('change', { bubbles: true });
      target.checked = true;
      target.dispatchEvent(evt);
    };
    this.el.addEventListener('transitionend', animationFinished);
  }

  toggleSubPanel(event, button) {
    const parentAccordion = button.closest('[data-accordion-item]');

    if (parentAccordion) {
      const checkbox = parentAccordion.querySelector('input');
      if (checkbox) checkbox.checked = false;

      parentAccordion.classList.remove('is-open');
      const parentPanel = parentAccordion.closest('[data-menu-panel]');
      
      if (parentPanel) {
        parentPanel.classList.remove('has-open-submenu');
        const checkboxes = parentPanel.querySelectorAll('input:checked');
        checkboxes.forEach((checkbox) => {
          checkbox.checked = false;
          const accordion = checkbox.closest('[data-accordion-item]');
          if (accordion) accordion.classList.remove('is-open');
        });
      }

      // Focus visible only when using keyboard
      const options = { focusVisible: event.pointerType === "" };

      checkbox.focus(options);
    }

    // TODO: Check if this is still needed
    // const targetSelector = button.getAttribute('data-drawer-submenu-close');
    // if (!targetSelector) {
    //   return;
    // }

    // const input = document.getElementById(targetSelector);
    // if (!input) {
    //   return;
    // }

    // input.focus();
  }
}

if (customElements.get('drawer-menu') === undefined) {
  customElements.define('drawer-menu', DrawerMenu);
}
